import {
	CERTIFICATION_SIGNATURE_TYPE_CODES,
	getCertificationSignatureTypeName,
} from "views/Eventos/utils/certification";
import {
	createEmptySignatureForm,
	INSTITUTIONAL_SIGNATURE_CODES,
} from "./constants";

export const isInstitutionalSignature = signatureTypeCode =>
	INSTITUTIONAL_SIGNATURE_CODES.includes(signatureTypeCode);

export const isTeacherSpeakerSignature = signatureTypeCode =>
	signatureTypeCode === CERTIFICATION_SIGNATURE_TYPE_CODES.DOCENTE_PONENTE;

export const formatDate = timestamp => {
	if (!timestamp || timestamp === -1) return "-";
	return new Date(timestamp).toLocaleDateString("es-PE");
};

export const getValidityText = ({
	validFrom,
	validTo,
	isIndefiniteValidity,
}) => {
	if (validFrom === -1) return "Sin vigencia";
	if (isIndefiniteValidity)
		return `Desde ${formatDate(validFrom)} - indeterminada`;
	if (validTo === -1 || !validTo) return `Desde ${formatDate(validFrom)}`;
	return `${formatDate(validFrom)} al ${formatDate(validTo)}`;
};

export const mapSignatureToForm = signature => {
	if (!signature) return createEmptySignatureForm();
	return {
		signatureTypeCode: signature.signatureTypeCode || "",
		campusIds: signature.campusIds || [],
		utpCode: signature.utpCode || "",
		displayName: signature.displayName || "",
		signatureFile: signature.signatureFile || {
			name: "",
			size: 0,
			base64: "",
			contentType: "",
			url: "",
			path: "",
			bucketName: "",
			checksum: "",
			urlExpiresAt: null,
			rawFile: null,
		},
		isIndefiniteValidity: Boolean(signature.isIndefiniteValidity),
		validFrom: signature.validFrom || -1,
		validTo: signature.validTo || -1,
		isActive:
			typeof signature.isActive === "boolean" ? signature.isActive : true,
	};
};

export const normalizeCampusIdsBySignatureType = (
	signatureTypeCode,
	campusIds = []
) => {
	if (!isInstitutionalSignature(signatureTypeCode)) return [...campusIds];
	return campusIds.length > 0 ? [campusIds[0]] : [];
};

export const normalizeFileName = (fileName = "") => {
	const extensionIndex = fileName.lastIndexOf(".");
	const name = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
	const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : "";
	const normalizedName = name
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-zA-Z0-9_-]+/g, "-")
		.replace(/^-+|-+$/g, "");

	return `${normalizedName || "firma"}${extension}`;
};

export const normalizeSignaturePayload = form => ({
	signatureTypeCode: form.signatureTypeCode,
	campusIds: normalizeCampusIdsBySignatureType(
		form.signatureTypeCode,
		form.campusIds
	),
	utpCode: form.utpCode.trim(),
	displayName: form.displayName.trim(),
	signatureFile: {
		name: form.signatureFile?.rawFile
			? normalizeFileName(form.signatureFile.name)
			: form.signatureFile?.name || "",
		base64: "",
		contentType: form.signatureFile?.contentType || "",
		path: form.signatureFile?.path || "",
		bucketName: form.signatureFile?.bucketName || "",
		checksum: form.signatureFile?.checksum || "",
	},
	isIndefiniteValidity: form.isIndefiniteValidity,
	validFrom: form.validFrom,
	validTo: form.isIndefiniteValidity ? null : form.validTo,
	isActive: form.isActive,
});

export const getCampusNamesByIds = (campusIds = [], allCampus = []) => {
	return campusIds
		.map(campusId => allCampus.find(({ id }) => id === campusId)?.name)
		.filter(Boolean);
};

export const getSignatureTypeLabel = signatureTypeCode =>
	getCertificationSignatureTypeName(signatureTypeCode);

const rangesOverlap = (left, right) => {
	const leftStart = left.validFrom || -1;
	const rightStart = right.validFrom || -1;
	const leftEnd =
		left.isIndefiniteValidity || !left.validTo
			? Number.MAX_SAFE_INTEGER
			: left.validTo;
	const rightEnd =
		right.isIndefiniteValidity || !right.validTo
			? Number.MAX_SAFE_INTEGER
			: right.validTo;
	return leftStart <= rightEnd && rightStart <= leftEnd;
};

export const getInstitutionalConflictItems = ({
	form,
	list = [],
	currentId = 0,
}) => {
	if (!isInstitutionalSignature(form.signatureTypeCode) || !form.isActive)
		return [];
	return list.filter(item => {
		if (item.id === currentId) return false;
		if (!item.isActive) return false;
		if (item.signatureTypeCode !== form.signatureTypeCode) return false;
		const hasSharedCampus = item.campusIds.some(campusId =>
			form.campusIds.includes(campusId)
		);
		if (!hasSharedCampus) return false;
		return rangesOverlap(form, item);
	});
};
